import { useCallback } from "react";

import {
  faArrowDownToLine,
  faCircleNotch,
  faPen,
  faPencilSlash,
  faVial,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { m } from "framer-motion";
import { useController, useForm } from "react-hook-form";
import useMeasure from "react-use-measure";

import { DatasetPurpose } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Label } from "src/base-components/Label";
import { AddDatasetCard } from "src/datasets/DatasetList/addDatasetModal/AddDatasetCard";
import { DecideDropzone } from "src/datasets/Dropzone";
import { useDownloadTemplate } from "src/datasets/utils";
import { Modal } from "src/design-system/Modal";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";

type AddDatasetForm = {
  purpose?: DatasetPurpose;
  method?: "fromScratch" | "assembleFromHistory" | "upload";
};

const getDefaultValues = (canAccessSimulations: boolean): AddDatasetForm => ({
  purpose: canAccessSimulations ? undefined : "test_run",
  method: undefined, //reset method and purpose when modal is closed
});

const SmoothResizeWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // The height of this component is animated as
  // the creation method section appears or disappears
  const [ref, { height }] = useMeasure({
    offsetSize: true,
  });
  // Avoids scrollbar flickering
  const EXTRA_HEIGHT = 0;
  return height !== 0 ? (
    <m.div
      animate={{
        height: height + EXTRA_HEIGHT,
      }}
      initial={false}
    >
      <div ref={ref}>{children}</div>
    </m.div>
  ) : (
    <div ref={ref}>{children}</div>
  );
};

const UploadCard: React.FC<{
  onFilesDropped: (files: File[], errors: string[]) => Promise<void>;
  afterFileDropped: () => void;
}> = ({ onFilesDropped, afterFileDropped }) => {
  const handleFilesDropped = async (files: File[], errors: string[]) => {
    await onFilesDropped(files, errors);
    afterFileDropped();
  };

  return (
    <DecideDropzone
      content={({ getInputProps, getRootProps }) => (
        <AddDatasetCard {...getRootProps()} dataLoc="dataset-upload-card">
          <AddDatasetCard.Title>
            Upload or drag & drop a file
          </AddDatasetCard.Title>
          <AddDatasetCard.Description>
            Import test data from a CSV, JSON, or XLSX file
          </AddDatasetCard.Description>
          <input {...getInputProps()} />
        </AddDatasetCard>
      )}
      onSubmitFiles={handleFilesDropped}
    />
  );
};

export const AddDatasetModal: React.FC<{
  onFilesDropped: (files: File[], errors: string[]) => Promise<void>;
  onAssemble: (type: DatasetPurpose) => void;
  onCreate: () => void;
  onClose: () => void;
  open: boolean;
}> = ({ onFilesDropped, onAssemble, onCreate, onClose, open }) => {
  const downloadTemplate = useDownloadTemplate();
  const simulationAccess = useSimulationAccess();
  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState,
  } = useForm<AddDatasetForm>({
    defaultValues: {
      purpose: simulationAccess.canAccess ? undefined : "test_run",
      method: undefined,
    },
  });

  const {
    field: { value: purposeValue, onChange: _setPurposeValue },
  } = useController({
    name: "purpose",
    control,
  });

  const {
    field: { value: methodValue, onChange: setMethodValue },
  } = useController({
    name: "method",
    control,
  });

  const setPurposeValue = useCallback(
    (purpose: DatasetPurpose) => {
      _setPurposeValue(purpose);
      setMethodValue(undefined);
    },
    [_setPurposeValue, setMethodValue],
  );

  const onSubmit = (data: AddDatasetForm) => {
    if (data.method === "assembleFromHistory" && data.purpose) {
      onAssemble(data.purpose);
    } else if (data.method === "fromScratch") {
      onCreate();
    }
    onClose();
  };

  return (
    <Modal
      afterLeave={() => resetForm(getDefaultValues(simulationAccess.canAccess))}
      open={open}
      onClose={onClose}
    >
      <Modal.Header>Create Dataset</Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <SmoothResizeWrapper>
            {simulationAccess.canAccess && (
              <>
                <Label mb="mb-4">Dataset type</Label>
                <div className="mb-6 space-y-4">
                  <AddDatasetCard
                    dataLoc="test-dataset-card"
                    selected={purposeValue === "test_run"}
                    onClick={() => setPurposeValue("test_run")}
                  >
                    <AddDatasetCard.Title>Test dataset</AddDatasetCard.Title>
                    <AddDatasetCard.Detail icon={faPlay}>
                      Lightweight tests and fast iteration
                    </AddDatasetCard.Detail>
                    <AddDatasetCard.Detail icon={faPen}>
                      Editable
                    </AddDatasetCard.Detail>
                  </AddDatasetCard>
                  <AddDatasetCard
                    dataLoc="simulation-dataset-card"
                    selected={purposeValue === "simulation"}
                    onClick={() => setPurposeValue("simulation")}
                  >
                    <AddDatasetCard.Title>
                      Simulation dataset
                    </AddDatasetCard.Title>
                    <AddDatasetCard.Detail icon={faVial}>
                      Large-scale simulations and extensive analyses
                    </AddDatasetCard.Detail>
                    <AddDatasetCard.Detail icon={faPencilSlash}>
                      Non-editable
                    </AddDatasetCard.Detail>
                  </AddDatasetCard>
                </div>
              </>
            )}
            {/* keep card order in sync with DatasetDropdown items */}
            {purposeValue && (
              <>
                <Label mb="mb-4">Creation method</Label>
                <div className="space-y-3">
                  {purposeValue === "test_run" && (
                    <AddDatasetCard
                      dataLoc="create-from-scratch-card"
                      selected={methodValue === "fromScratch"}
                      onClick={() => setMethodValue("fromScratch")}
                    >
                      <AddDatasetCard.Title>From scratch</AddDatasetCard.Title>
                      <AddDatasetCard.Description>
                        Start creating row by row
                      </AddDatasetCard.Description>
                    </AddDatasetCard>
                  )}
                  <AddDatasetCard
                    dataLoc="assemble-from-history-card"
                    selected={methodValue === "assembleFromHistory"}
                    onClick={() => setMethodValue("assembleFromHistory")}
                  >
                    <AddDatasetCard.Title>
                      From historical decisions
                    </AddDatasetCard.Title>
                    <AddDatasetCard.Description>
                      Sample from your past decisions to create a representative
                      dataset
                    </AddDatasetCard.Description>
                  </AddDatasetCard>
                  {purposeValue === "test_run" && (
                    <UploadCard
                      afterFileDropped={() => onClose()}
                      onFilesDropped={onFilesDropped}
                    />
                  )}
                </div>
                {purposeValue === "test_run" && (
                  <div className="mt-2.5 flex items-center gap-6">
                    <button
                      className="flex cursor-pointer items-center gap-1 rounded-sm transition-colors"
                      data-loc="download-csv-template-button"
                      type="button"
                      onClick={() => {
                        downloadTemplate("csv");
                      }}
                    >
                      <Icon
                        color="text-gray-500"
                        icon={faArrowDownToLine}
                        size="xs"
                      />
                      <span className="font-inter-normal-12px text-gray-500">
                        Download CSV template
                      </span>
                    </button>
                    <button
                      className="flex cursor-pointer items-center gap-1 rounded-sm transition-colors"
                      data-loc="download-json-template-button"
                      type="button"
                      onClick={() => {
                        downloadTemplate("json");
                      }}
                    >
                      <Icon
                        color="text-gray-500"
                        icon={faArrowDownToLine}
                        size="xs"
                      />
                      <span className="font-inter-normal-12px text-gray-500">
                        Download JSON template
                      </span>
                    </button>
                  </div>
                )}
              </>
            )}
          </SmoothResizeWrapper>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="create-dataset-option-modal-confirm"
              disabled={!(purposeValue && methodValue)}
              htmlType="submit"
              variant="primary"
              fullWidth
            >
              {formState.isSubmitting ? (
                <FontAwesomeIcon icon={faCircleNotch} width={12} spin />
              ) : (
                "Create dataset"
              )}
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
